import React from "react";
import { TextField, useTheme } from "cayo.ui";
import { PrimaryButton, Spinner, MessageBar, MessageBarType } from "@fluentui/react";
import styled from "styled-components";
import { useIntl, FormattedMessage } from "react-intl";
import { communicationChannelsWizardMessages } from "../../shared/messages";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";

type SelectUserAccountProps = {
  accountName: string;
  grantState: string;
  error: string;
  grantIsActive: boolean;
  processing: boolean;
  accountMissMatch: boolean;
  handleChange: (key: string, value: string | boolean) => void;
  onGrant: () => void;
  resetError: () => void;
};

export const SelectUserAccount = ({
  accountName,
  grantIsActive,
  grantState,
  processing,
  error,
  accountMissMatch,
  handleChange,
  onGrant,
  resetError,
}: SelectUserAccountProps) => {
  const intl = useIntl();
  const { cayoTheme } = useTheme();

  const options: IChoiceGroupOption[] = [
    {
      key: "true",
      text: intl.formatMessage(
        communicationChannelsWizardMessages.selectUserAccountOptionRecommended
      ),
      ariaLabel: intl.formatMessage(
        communicationChannelsWizardMessages.selectUserAccountOptionRecommended
      ),
      onRenderField: (props, render) => {
        return (
          <div>
            {render!(props)}
            {props!.checked && (
              <GrantWrap>
                <TextField
                  label={intl.formatMessage(communicationChannelsWizardMessages.userAccountField)}
                  required
                  value={accountName}
                  setValue={(value) => handleChange("name", value)}
                />

                <Wrapper>
                  <GrantState className={grantState}>
                    <span className="consent">Consent:</span>
                    {processing ? <Spinner label={""} labelPosition="right" /> : `${grantState}`}
                  </GrantState>
                  <PrimaryButton
                    text={intl.formatMessage(communicationChannelsWizardMessages.grant)}
                    onClick={onGrant}
                    disabled={!grantIsActive}
                    styles={{ root: { marginTop: 10 } }}
                  />
                </Wrapper>
              </GrantWrap>
            )}
          </div>
        );
      },
    },
    {
      key: "false",
      text: intl.formatMessage(
        communicationChannelsWizardMessages.selectUserAccountOptionNotRecommended
      ),
      ariaLabel: intl.formatMessage(
        communicationChannelsWizardMessages.selectUserAccountOptionNotRecommended
      ),
      onRenderField: (props, render) => render!(props),
    },
  ];

  const handleChangeConfig = (value: string) => {
    resetError();
    handleChange("use", value === "true");
  };

  return (
    <div>
      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            margin: "10px 0 10px 0",
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        <FormattedMessage
          id={communicationChannelsWizardMessages.selectUserAccountText.id}
          defaultMessage={communicationChannelsWizardMessages.selectUserAccountText.defaultMessage}
        />
      </MessageBar>
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          truncated={true}
          styles={{
            root: {
              margin: "10px 0 10px 0",
            },
          }}
        >
          {error}
        </MessageBar>
      )}
      {accountMissMatch && (
        <MessageBar
          messageBarType={MessageBarType.error}
          truncated={true}
          styles={{
            root: {
              margin: "10px 0 10px 0",
            },
          }}
        >
          {intl.formatMessage(communicationChannelsWizardMessages.userAccountMissMatch)}
        </MessageBar>
      )}
      <ChoiceGroup
        defaultSelectedKey="true"
        options={options}
        onChange={(_, option) => handleChangeConfig(option!.key)}
      />
    </div>
  );
};

const GrantWrap = styled.div`
  padding: 10px 0;
`;

const Wrapper = styled.div`
    display: flex; 
    gap 15px;
    align-items: center;
`;

const GrantState = styled.span`
  font-weight: 500;
  display: flex; 
  gap 10px;
  align-items: center;

  .consent {
    color: rgb(50, 49, 48) !important;
  }

  &.Granted {
    color: ${(props) => props.theme.cayoTheme.brandColors.success};
  }

  &.Invalid {
    color: ${(props) => props.theme.cayoTheme.brandColors.error};
  }
`;
